// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { pluralize } from '@/utilities/pluralize';

interface FormatDurationOptions {
  short?: boolean;
}

/**
 * Formats a duration in seconds to a human-readable string.
 * Handles hours, minutes, and seconds.
 *
 * @param seconds - The duration in seconds
 * @param options.short - If true, abbreviates units: "secs", "mins" (hours unchanged)
 * @returns A formatted string like "5 seconds", "1 minute", "1 hour, 30 minutes"
 */
export function formatDuration(
  seconds: number,
  options: FormatDurationOptions = {}
): string {
  const { short = false } = options;

  if (seconds <= 0) {
    return pluralize(0, {
      singular: short ? 'sec' : 'second',
      plural: short ? 'secs' : 'seconds',
    });
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(pluralize(hours, { singular: 'hour' }));
  }

  if (minutes > 0) {
    parts.push(
      pluralize(minutes, {
        singular: short ? 'min' : 'minute',
        plural: short ? 'mins' : 'minutes',
      })
    );
  }

  if (remainingSeconds > 0 || parts.length === 0) {
    parts.push(
      pluralize(remainingSeconds, {
        singular: short ? 'sec' : 'second',
        plural: short ? 'secs' : 'seconds',
      })
    );
  }

  return parts.join(', ');
}
